import * as Icon from "@iyk/icons"
import * as React from "react"
import * as Accordion from "./primitives/accordion.tsx"

import { cva } from "./cva.ts"

// #region Root

export const Root = React.forwardRef(({ className, ...props }: Accordion.RootProps, ref: Ref) => {
  return <Accordion.Root ref={ref} className={classForRoot({ className })} {...props} />
})

export const classForRoot = cva(["flex flex-col bshadow-t bshadow-gray-6 text-sm"])

// #endregion

// #region Item

export const Item = React.forwardRef(({ className, ...props }: Accordion.ItemProps, ref: Ref) => {
  return <Accordion.Item ref={ref} className={classForItem({ className })} {...props} />
})

export const classForItem = cva(["bshadow-b bshadow-gray-6"])

// #endregion

// #region Heading

export const Heading = React.forwardRef(
  ({ children, className, ...props }: Accordion.HeadingProps, ref: Ref) => {
    const state = Accordion.useItemContext()
    const Decoration = state.open ? Icon.Minus : Icon.Plus

    return (
      <Accordion.Heading ref={ref} className={classForHeading({ className })} {...props}>
        {children}
        <Decoration className="relative top-0.5" />
      </Accordion.Heading>
    )
  },
)

export const classForHeading = cva([
  "flex justify-between w-full",
  "py-4",
  "cursor-default",
  "touch:outline-none",
])

// #endregion

// #region Content

export const Content = React.forwardRef(
  ({ className, ...props }: Accordion.ContentProps, ref: Ref) => {
    const { open } = Accordion.useItemContext()
    const contentRef = React.useRef<HTMLElement | null>(null)
    const cache = React.useRef(open)

    React.useImperativeHandle(ref, () => contentRef.current!)

    React.useEffect(() => {
      const { current } = contentRef

      const classForContentOpened = classForContent({ open: true })
      const classForContentClosed = classForContent({ open: false })

      if (current) {
        current.className = open ? classForContentOpened : classForContentClosed

        animationForContent(current, open, cache, classForContentOpened, classForContentClosed)
      }
    }, [open])

    return (
      <Accordion.Content
        className={classForContent({
          open,
          className,
        })}
        ref={contentRef}
        {...props}
      />
    )
  },
)

export const classForContent = cva(["pb-4", "text-gray-11", "transition-all delay-200"], {
  variants: {
    open: {
      true: ["block"],
      false: ["hidden"],
    },
  },
})

const animationForContent = (
  current: HTMLElement,
  open: boolean,
  cache: React.MutableRefObject<boolean>,
  classForContentOpened: string,
  classForContentClosed: string,
) => {
  if (open !== cache.current) {
    cache.current = open

    current.className = classForContentOpened

    const { display, padding, transitionDelay } = getComputedStyle(current)

    current
      .animate(
        [
          {
            display,
          },
        ],
        {
          fill: "both",
        },
      )
      .finished.then((animation) => {
        const height = current.clientHeight + "px"

        current.className = open ? classForContentOpened : classForContentClosed

        animation.cancel()

        current.animate(
          [
            {
              display,
              height: open ? "0px" : height,
              padding: open ? "0px" : padding,
              overflow: "hidden",
            },
            {
              display,
              height: open ? height : "0px",
              padding: open ? padding : "0px",
              overflow: "hidden",
            },
          ],
          {
            duration: parseFloat(transitionDelay) * 1000,
            fill: "none",
          },
        )
      })
  }
}

// #endregion

// #region Types

type Ref = React.ForwardedRef<HTMLElement>

// #endregion
